import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject } from 'rxjs';
import { DataTableComponent } from '../../../../../shared/components/data-table/data-table.component';
import { ClaimRow } from '../../../../claims/models/Claim';
import { DataTableColumn } from '../../../../../shared/components/data-table/DataTableColumn.model';
import { ClaimsService } from '../../../../claims/services/claims.service';
import { SearchService } from '../../../../../shared/search/search.service';
import { ClaimStatusColor } from '../../../../claims/claim-detail/ClaimDetailConstants';
import { dateFormat } from '../../../../../utilities/DateUtility';
import { formatCurrencyString } from '../../../../../utilities/CurrencyUtility';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../../infrastructure/context/user-context-provider';
import { SortParams } from '../../../../../shared/search/SearchParams';

@Component({
  selector: 'app-property-claims',
  imports: [
    CommonModule,
    MatCardModule,
    DataTableComponent,
    MatButtonModule
  ],
  templateUrl: './property-claims.component.html',
  styleUrl: './property-claims.component.scss'
})
export class PropertyClaimsComponent implements OnDestroy {

  @Input() propertyId!: number;
  @Input() propertyOrganizationId!: number;

  claimsColumns: DataTableColumn[] = [
    { column: 'id', display: 'Claim ID' },
    { column: 'organizationId', display: 'Organization ID' },
    { column: 'reservationId', display: 'Reservation ID' },
    { column: 'propertyName', display: 'Property Name' },
    { column: 'firstName', display: 'First Name' },
    { column: 'lastName', display: 'Last Name' },
    { column: 'reservationDates', display: 'Reservation Dates' },
    { column: 'estimateAmount', display: 'Estimated Claim Amount' },
    { column: 'actualAmount', display: 'Amount Paid' },
    { column: 'pillStatus', display: 'Status' }
  ];

  tableToApiColumnMap: { [key: string]: string } = {
    'id': 'claimId',
    'organizationId': 'organizationId',
    'reservationId': 'reservationId',
    'propertyName': 'propertyName',
    'firstName': 'firstName',
    'lastName': 'lastName',
    'reservationDates': 'arrivalDate',
    'estimateAmount': 'estimateAmount',
    'actualAmount': 'actualAmount',
    'pillStatus': 'status'
  }

  loading = new BehaviorSubject<boolean>(false);
  claimsDataSource = new MatTableDataSource<ClaimRow>();
  claimsTableLength = 0;
  claimsPageSize = 10;
  claimsPageIndex = 0;

  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);

  constructor(
    private claimService: ClaimsService,
    private searchService: SearchService,
    private logService: Logger,
    private userContextProvider: UserContextProvider
  ) { }

  ngOnDestroy() {
    this.searchService.removeSearchAndSortQueries();
    this.searchService.resetQuery();
  }

  loadPropertyClaimsTab() {
    this.searchService.removeSearchAndSortQueries();
    this.searchService.resetQuery();
    this.searchService.setPageSize(this.claimsPageSize);
    this.loadPropertyClaims();
  }

  loadPropertyClaims() {
    this.findClaimsByPropertyId(this.propertyId);
  }

  async findClaimsByPropertyId(propertyId: any) {
    try {
      this.loading.next(true);
      this.claimsDataSource.data = [];

      this.searchService.setOrganizationId(this.propertyOrganizationId);
      this.searchService.replaceFilter({ fieldName: 'propertyIdText', value: [String(propertyId)], range: false });
      const { claims, totalRecords } = await this.claimService.searchClaims(this.searchService.currentQuery);
      const mappedClaims = claims.map((claim: any) => this.buildClaimRow(claim));
      this.updateClaimsTableDataSource(mappedClaims, totalRecords);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Property Claims', 'Error while retrieving Property Claims');
    } finally {
      this.loading.next(false);
    }
  }

  async updateClaimsTableDataSource(claims: ClaimRow[], totalRecords: number) {
    this.claimsDataSource = new MatTableDataSource(claims);
    this.claimsTableLength = totalRecords;
  }

  buildClaimRow(claim: any): ClaimRow {
    let reservationStart = '';
    let reservationEnd = '';

    try {
      reservationStart = dateFormat(claim.arrivalDate, this.userContextProvider.region);
      reservationEnd = dateFormat(claim.departureDate, this.userContextProvider.region);
    } catch (e) {
      this.logService.handleError(`Could not convert Reservation Arrival Date: ${claim.arrivalDate} or Departure Date: ${claim.departureDate}`, `Could not convert Reservation Arrival or Departure Date`, `Could not convert Reservation Arrival Date: ${claim.arrivalDate} or Reservation Departure Date: ${claim.departureDate}`);
    }

    return {
      id: claim.claimId,
      referenceId: '',
      organizationId: claim.organizationId,
      organizationName: claim.organizationName,
      reservationId: claim.reservationId,
      propertyName: claim.propertyName,
      firstName: claim.firstName,
      lastName: claim.lastName,
      reservationDates: `${reservationStart} - ${reservationEnd}`,
      arrivalDate: reservationStart,
      estimateAmount: formatCurrencyString(claim.estimateAmount, this.userContextProvider.currencyCode),
      actualAmount: formatCurrencyString(claim.actualAmount, this.userContextProvider.currencyCode),
      pillStatus: claim.status
    }
  }

  onSortEvent(sortParams: SortParams) {
    this.addSortToQuery(sortParams);
    this.loadPropertyClaims();
  }

  private addSortToQuery(sortParams: SortParams) {
    sortParams.sortField = this.tableToApiColumnMap[sortParams.sortField];
    this.searchService.addSort(sortParams);
  }

  onPageEvent(event: PageEvent): void {
    this.claimsPageSize = event.pageSize;
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadPropertyClaims();
  }

  getClaimStatusColor = (status?: string): string => {
    switch (status) {
      case "Draft":
        return ClaimStatusColor.DRAFT;
      case "In Progress":
        return ClaimStatusColor.INPROGRESS;
        case "Pending":
          return ClaimStatusColor.PENDING;
      case "Completed":
        return ClaimStatusColor.COMPLETED;
      default:
        return ClaimStatusColor.PENDING;
    }
  }
}